import { useApolloClient } from '@apollo/client'
import { F0AnalyticsDashboard, type DashboardChartItem } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  GetPostAnalyticsDocument,
  GetPostMemberStatusesDocument,
  type GetPostMemberStatusesQuery,
} from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useParams } from 'react-router-dom'
import { Box } from '@factorialco/deprecated-design-system'

type MemberStatusNode =
  GetPostMemberStatusesQuery['posts']['postMemberStatusesConnection']['nodes'][number]

const PostInsightsPage = () => {
  const client = useApolloClient()
  const id = useParams().id ?? ''

  const MEMBER_STATUSES = {
    NOT_SEEN: {
      label: i18n.t('posts.communities.insights.member_status.not_seen'),
      color: 'lilac',
    },
    SEEN: { label: i18n.t('posts.communities.insights.member_status.seen'), color: 'purple' },
    OPENED: { label: i18n.t('posts.communities.insights.member_status.opened'), color: 'viridian' },
  }

  const funnelChartItem: DashboardChartItem = {
    id: 'audience-bar',
    title: i18n.t('posts.communities.insights.audience'),
    type: 'chart',
    colSpan: 3,
    chart: {
      type: 'bar',
      orientation: 'horizontal',
      showLegend: false,
    },
    fetchData: async () => {
      const { data } = await client.query({
        query: GetPostAnalyticsDocument,
        variables: { postId: id },
      })

      const analytics = data.posts.postAnalyticsConnection.nodes[0]

      if (!analytics) {
        return { categories: [], series: [] }
      }

      const {
        totalGroupMembersCount: total,
        uniqueViewersCount: impressions,
        uniqueVisitorsCount: visits,
      } = analytics

      return {
        categories: [
          i18n.t('posts.communities.insights.member_status.opened'),
          i18n.t('posts.communities.insights.member_status.seen'),
          i18n.t('posts.communities.insights.total'),
        ],
        series: [
          {
            name: i18n.t('posts.communities.insights.audience'),
            data: [
              { value: visits, target: impressions, color: MEMBER_STATUSES.OPENED.color },
              { value: impressions, target: total, color: MEMBER_STATUSES.SEEN.color },
              { value: total, target: total, color: MEMBER_STATUSES.NOT_SEEN.color },
            ],
          },
        ],
      }
    },
  }

  const source = useGraphqlDataCollectionSource({
    document: GetPostMemberStatusesDocument,
    mapVariables: ({ filters }) => {
      const statusValues = Array.isArray(filters.status) ? filters.status : []
      return {
        postId: id,
        seen: statusValues.includes('notSeen') ? false : undefined,
        opened: statusValues.includes('notOpened') ? false : undefined,
      }
    },
    mapData: (data: GetPostMemberStatusesQuery) =>
      data?.posts?.postMemberStatusesConnection?.nodes ?? [],
    mapPagination: (data: GetPostMemberStatusesQuery) => ({
      total: data?.posts?.postMemberStatusesConnection?.totalCount ?? 0,
    }),
    paginationType: 'pages',
    filters: {
      status: {
        type: 'in' as const,
        label: i18n.t('posts.communities.insights.status'),
        options: {
          options: [
            {
              value: 'notSeen',
              label: i18n.t('posts.communities.insights.member_status.not_seen'),
            },
            {
              value: 'notOpened',
              label: i18n.t('posts.communities.insights.member_status.not_opened'),
            },
          ],
        },
      },
    },
    presets: [
      {
        label: i18n.t('posts.communities.insights.member_status.not_seen'),
        filter: { status: ['notSeen'] },
      },
      {
        label: i18n.t('posts.communities.insights.member_status.not_opened'),
        filter: { status: ['notOpened'] },
      },
    ],
  })

  return (
    <ResponsivePage.Body>
      <Box flexDirection="column" gap={'s12'}>
        <F0AnalyticsDashboard items={[funnelChartItem]} />
        <Box paddingX="s24" paddingTop="s24">
          <OneDataCollection
            source={source}
            visualizations={[
              {
                type: 'table',
                options: {
                  columns: [
                    {
                      id: 'name',
                      label: i18n.t('posts.communities.insights.name'),
                      width: 200,
                      render: (item: MemberStatusNode) => ({
                        type: 'person' as const,
                        value: {
                          firstName: item.employeeName?.firstName ?? '',
                          lastName: item.employeeName?.lastName ?? '',
                        },
                      }),
                    },
                    {
                      id: 'status',
                      label: i18n.t('posts.communities.insights.status'),
                      render: (item: MemberStatusNode) => ({
                        type: 'dotTag',
                        value: item.opened
                          ? MEMBER_STATUSES.OPENED
                          : item.seen
                            ? MEMBER_STATUSES.SEEN
                            : MEMBER_STATUSES.NOT_SEEN,
                      }),
                    },
                  ],
                },
              },
            ]}
          />
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default PostInsightsPage
